/**
 * cli360 — primitivos de apresentacao e formatadores compartilhados pela
 * pagina Cliente 360 (Detalhe do cliente em abas). Tudo renderiza dentro do
 * root .cli360 (skin escopada em styles/canonical/cliente360.css).
 *
 * Mantem as abas (VisaoGeral/Pedidos/Historico/Financeiro/Documentos) DRY:
 * Panel/Field/Stat/Tl sao iguais ao mockup aprovado; formatadores idem ao
 * que o orquestrador ja usava.
 */
import React from "react";
import { dataLocalDe } from "../../../utils/dinheiro";

// ---------- Formatadores ----------

export function fmtBRLCompacto(valor) {
  const n = Number(valor) || 0;
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `R$ ${Math.round(n / 1_000)}k`;
  return `R$ ${Math.round(n)}`;
}

export function fmtBRLCheio(valor) {
  const n = Number(valor) || 0;
  return `R$ ${Math.round(n).toLocaleString("pt-BR")}`;
}

export function fmtDataCurta(iso) {
  if (!iso) return "";
  try {
    return (dataLocalDe(iso) || new Date(NaN)).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch {
    return "";
  }
}

export function fmtDataLonga(iso) {
  if (!iso) return "";
  try {
    return (dataLocalDe(iso) || new Date(NaN)).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
}

export function fmtDataHora(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function prefixoHa(rotulo) {
  if (!rotulo) return "";
  if (rotulo === "hoje" || rotulo === "ontem") return rotulo;
  return `há ${rotulo}`;
}

export function whatsappUrl(telefone) {
  const digitos = (telefone || "").replace(/\D/g, "");
  return digitos ? `https://wa.me/${digitos}` : null;
}

/** Gradiente deterministico (mesma logica do header antigo, usando a paleta chart). */
export function avatarGradiente(nome) {
  let h = 0;
  for (const ch of nome || "x") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const a = (h % 8) + 1;
  let b = ((h >>> 3) % 8) + 1;
  if (b === a) b = (b % 8) + 1;
  return `linear-gradient(135deg, var(--chart-${a}), var(--chart-${b}))`;
}

// ---------- Primitivos visuais ----------

export function Panel({ title, icon, sub, action, children, style }) {
  const hasHead = title != null || action != null;
  return (
    <section className="panel" style={style}>
      {hasHead && (
        <div className="panel-head">
          <div className="sec-title">
            {icon}
            {title}
            {sub != null ? (
              <span className="tiny muted" style={{ fontWeight: 400, marginLeft: 2 }}>
                {sub}
              </span>
            ) : null}
          </div>
          {action || null}
        </div>
      )}
      {children}
    </section>
  );
}

// `atributos` (ex.: role/aria-label) vão para o contêiner do par rótulo-valor.
export function Field({ label, children, mono, cols, ...atributos }) {
  return (
    <div {...atributos} className="field" style={cols ? { gridTemplateColumns: cols } : undefined}>
      <span className="fl">{label}</span>
      <span className={`fv${mono ? " m" : ""}`}>{children}</span>
    </div>
  );
}

export function Stat({ label, value, sub, icon, tone, style }) {
  return (
    <div className="stat" style={style}>
      <div className="sl" style={tone === "danger" ? { color: "var(--danger-text)" } : undefined}>
        {icon}
        {label}
      </div>
      <div className={`sv${tone === "pos" ? " pos" : tone === "neg" ? " neg" : ""}`}>{value}</div>
      {sub != null ? (
        <div className="ss" style={tone === "danger" ? { color: "var(--danger-text)" } : undefined}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

export function Badge({ tone = "neutral", dot = false, children, style }) {
  return (
    <span className={`badge ${tone}${dot ? "" : " nd"}`} style={style}>
      {children}
    </span>
  );
}

export function ScorePill({ tone = "neutral", children }) {
  const map = {
    success: { background: "var(--success-bg)", color: "var(--success-text)" },
    warning: { background: "var(--warning-bg)", color: "var(--warning-text)" },
    danger: { background: "var(--danger-bg)", color: "var(--danger-text)" },
    neutral: { background: "var(--neutral-bg)", color: "var(--neutral-text)" },
    info: { background: "var(--info-bg)", color: "var(--info-text)" },
  };
  return (
    <span className="score-pill" style={map[tone] || map.neutral}>
      {children}
    </span>
  );
}

/** Timeline estilo Correios (linha vertical + nós). */
export function Tl({ children }) {
  return <div className="tl">{children}</div>;
}

export function TlItem({ tone, icon, title, titleExtra, text, time }) {
  return (
    <div className="tl-item">
      <div className={`tl-dot${tone ? ` ${tone}` : ""}`}>{icon}</div>
      {titleExtra ? (
        <div className="flex center" style={{ gap: 8 }}>
          <span className="tl-title">{title}</span>
          {titleExtra}
        </div>
      ) : (
        <div className="tl-title">{title}</div>
      )}
      {text != null ? <div className="tl-text">{text}</div> : null}
      {time != null ? <div className="tl-time">{time}</div> : null}
    </div>
  );
}

/** Placeholder honesto para aba cujo backend ainda nao existe. */
export function Soon({ icon, title, children }) {
  return (
    <div className="soon">
      {icon}
      <b>{title}</b>
      <div style={{ maxWidth: 460 }}>{children}</div>
    </div>
  );
}
